import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Droplets, Disc, Wrench, Package, ShoppingCart, Truck, Headphones,
  ArrowRight, ArrowLeft, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { PRODUCT_CATEGORIES, VALUE_PROPS } from '../data/siteData';
import { RecentlyViewed } from '../components/product/RecentlyViewed';
import { RicercaAvanzataWidget } from '../components/search/RicercaAvanzataWidget';
import { FeaturedProductCard } from '../components/product/FeaturedProductCard';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import api from '../services/api';
import type { Product } from '../types';
import { useAuth } from '../context/AuthContext';
import { useBanners } from '../hooks/useBanners';
import { bannerImageUrl, type Banner } from '../services/bannerService';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Droplets, Printer: Package, Disc, Wrench, Package, ShoppingCart, Truck, Headphones,
};

// ── Banner homepage ───────────────────────────────────────────────────────
// Gli asset arrivano da GET /api/banners (collection `banners`, gestibile
// dall'area amministrativa). Prima erano hardcoded qui e in public/, quindi
// sostituibili solo con un deploy.

const HERO_ROTATION_MS = 5000;

type BannerMediaState = 'loading' | 'ok' | 'error';

/**
 * Immagine di un banner, con i suoi tre stati.
 *
 * Il grigio (`bg-secondary/30` del contenitore, che si vede attraverso
 * l'immagine non ancora opaca) vale per il caricamento e per l'errore, mai
 * per uno Slot vuoto: un rettangolo grigio permanente somiglia a
 * un'immagine rotta, che è peggio di nessun banner. Uno Slot senza banner
 * collassa.
 *
 * Con `withMobile` la variante mobile è scelta dal browser via
 * `<source media>` e non da JavaScript: il confine dei 640px è dichiarato
 * in un posto solo e non c'è re-render al resize. Se `mobileImage` manca si
 * ricade sul desktop, che il box quadrato ritaglierà.
 */
function BannerMedia({ banner, withMobile = false }: { banner: Banner; withMobile?: boolean }) {
  const [state, setState] = useState<BannerMediaState>('loading');
  const desktop = bannerImageUrl(banner.desktopImage);
  const mobile = withMobile ? bannerImageUrl(banner.mobileImage) : null;

  // Il backend non pubblica i banner senza asset desktop; questa è solo la
  // rete di sicurezza per non rendere un box vuoto.
  if (!desktop) return null;

  return (
    <>
      <picture className="block w-full h-full">
        {/*
          `<source>` va scritto come elemento fisso, MAI dentro
          un'espressione JSX tipo `{mobile && <source/>}`.

          Il plugin di sviluppo `plugins/visual-edits` avvolge ogni figlio
          che sia un'espressione in uno `<span style="display:contents">`
          (vedi `wrapDynamicExpressionChildren`), e un `<source>` che non
          è figlio *diretto* di `<picture>` viene ignorato dal browser:
          `display:contents` cambia il layout, non l'algoritmo di
          selezione. Risultato: l'asset mobile non veniva mai servito.

          Quando la variante mobile manca si punta al desktop, che è lo
          stesso URL dell'`<img>`: nessuna richiesta in più, e il box
          quadrato lo ritaglia come previsto.
        */}
        <source media="(max-width: 639.98px)" srcSet={mobile ?? desktop} />
        <img
          src={desktop}
          alt={banner.alt}
          onLoad={() => setState('ok')}
          onError={() => setState('error')}
          className={`w-full h-full object-cover transition-opacity duration-300 motion-reduce:transition-none ${
            state === 'ok' ? 'opacity-100' : 'opacity-0'
          }`}
          draggable={false}
        />
      </picture>
      {/* In errore il testo alternativo diventa visibile: lo Slot resta
          grigio, ma almeno si legge cosa avrebbe dovuto mostrare. */}
      {state === 'error' && (
        <span className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-muted-foreground">
          {banner.alt}
        </span>
      )}
    </>
  );
}

/**
 * Contenitore cliccabile del banner.
 *
 * Senza `href` il banner è informativo e si rende come `<div>`: nessun
 * cursore pointer, nessun hover, nessun focus da tastiera. Un `<Link>` che
 * non porta da nessuna parte è una promessa non mantenuta.
 */
function BannerBox({
  banner, className, tabIndex, ariaHidden, testId, children,
}: {
  banner: Banner;
  className: string;
  tabIndex?: number;
  ariaHidden?: boolean;
  testId?: string;
  children: React.ReactNode;
}) {
  if (!banner.href) {
    return (
      <div className={className} aria-hidden={ariaHidden} data-testid={testId}>
        {children}
      </div>
    );
  }
  return (
    <Link
      to={banner.href}
      className={className}
      aria-label={banner.alt}
      tabIndex={tabIndex}
      aria-hidden={ariaHidden}
      data-testid={testId}
    >
      {children}
    </Link>
  );
}

/**
 * Slot `hero`: il box a destra del widget "Ricerca avanzata".
 *
 * Tre regimi di altezza:
 *
 *   < 640px      `aspect-square`    asset mobile, fallback desktop
 *   640–1023px   `aspect-[16/9]`    asset desktop
 *   ≥ 1024px     `lg:flex-1`: riempie la riga della griglia
 *
 * Sopra i 1024px l'altezza arriva dalla riga, che vale
 * `max(widget, 16:9)`: il widget resta la misura di riferimento
 * (decisione del 24/02/2026) ma la colonna dichiara anche un pavimento
 * `lg:aspect-[16/9]`, così il box non scende sotto il rapporto degli
 * asset. Misurato: box 640 × 414 a 1024px (comanda il widget, 414px) e
 * 864 × 486 da 1440px in su (comanda il 16:9).
 *
 * Il pavimento è *dichiarato* e non ereditato dalle immagini. Prima il
 * box prendeva l'altezza dal contenuto, quindi due slide con rapporti
 * diversi facevano alzare lo slot dalla più alta e ritagliare le altre.
 *
 * `object-cover` invece di `object-contain`: con `contain`, a 1024px il
 * 14% dello Slot restava fondo scoperto. Il ritaglio che `cover`
 * introduce vale solo fra 1024 e ~1180px, dove il widget è più alto di un
 * 16:9 — al massimo il 6,5% per lato in orizzontale, assorbito dall'area
 * di sicurezza degli asset (asset-spec.md §2.1). In verticale non
 * ritaglia a nessuna larghezza.
 *
 * `min-h-[200px]` è ormai inerte (ogni box dichiarato è più alto) e resta
 * solo come rete di sicurezza.
 */
function HeroBanners({ banners }: { banners: Banner[] }) {
  // `audience` è filtrato server-side, quindi qui non esiste più il caso
  // speciale "anonimo → indice 0": si ruota se arrivano più banner, si sta
  // fermi se ne arriva uno.
  const rotates = banners.length > 1;

  // Embla gestisce scorrimento, swipe su touch e loop; il plugin Autoplay la
  // rotazione. Con un solo banner il carosello è spento (`active: false`):
  // niente drag, niente timer. Il passo si annulla con prefers-reduced-motion
  // (`duration: 0` è il salto istantaneo di Embla).
  //
  // La pausa al passaggio del mouse riprende all'uscita
  // (`stopOnInteraction: false`), come prima di Embla; lo stesso vale per lo
  // swipe, che ferma il timer solo finché il dito è giù. Il mouse si ascolta
  // sul box esterno (`rootNode`) e non sul viewport di Embla: frecce e dot
  // sono fratelli del viewport, e sopra di loro la rotazione deve fermarsi.
  const [viewportRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      active: rotates,
      breakpoints: { '(prefers-reduced-motion: reduce)': { duration: 0 } },
    },
    [Autoplay({
      delay: HERO_ROTATION_MS,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
      rootNode: viewport => viewport.parentElement ?? viewport,
    })],
  );

  const [current, setCurrent] = useState(0);

  // `reInit` copre il cambio della lista sotto i piedi (login, modifica da
  // admin): Embla riallinea l'indice e i dot lo seguono.
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrent(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => { emblaApi.off('select', onSelect).off('reInit', onSelect); };
  }, [emblaApi]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const goTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  return (
    <div
      className="relative w-full lg:flex-1 overflow-hidden rounded-xl bg-secondary/30 select-none min-h-[200px] aspect-square sm:aspect-[16/9] lg:aspect-auto"
      data-testid={rotates ? 'homepage-banner-carousel' : 'homepage-banner-static'}
    >
      {/*
        Viewport di Embla fuori dal flusso (`absolute inset-0`), il track
        dentro è `h-full`.

        Sopra i 1024px il box non ha un rapporto dichiarato: l'altezza
        arriva dal flex, cioè dal widget a sinistra. Ma finché le immagini
        stavano nel flusso, la loro altezza intrinseca risaliva la catena —
        durante il dimensionamento della riga della griglia l'altezza della
        colonna è indefinita, quindi `flex-basis: 0%` ricade sulla
        dimensione del contenuto e nemmeno `min-h-0` lo impedisce. Bastava
        una slide con rapporto diverso dalle altre (misurato: 1,499 contro
        16:9) perché la più alta portasse lo slot da 482 a 576px, tirandosi
        dietro la riga della griglia e quindi il widget, e facendo ritagliare
        le slide con il rapporto giusto.

        Fuori dal flusso il box non ha contenuto che lo alzi: l'altezza è
        solo quella del widget, come da specifica, e `object-cover` ritaglia
        ogni slide nello stesso box indipendentemente dal suo rapporto.
      */}
      <div className="absolute inset-0" ref={viewportRef}>
        {/* `x-excluded`: stessa trappola del `<source>` in BannerMedia. In
            sviluppo `plugins/visual-edits` avvolgerebbe il `.map` in uno
            `<span style="display:contents">`, ed Embla, che prende come
            slide i figli *diretti* del container, ne vedrebbe una sola: il
            carosello resta fermo. L'attributo dice al plugin di non
            toccare i figli di questo elemento. */}
        <div className="flex h-full" x-excluded="true">
          {banners.map((b, i) => (
            <BannerBox
              key={b.id}
              banner={b}
              className="relative block h-full min-w-0 flex-[0_0_100%]"
              tabIndex={i === current ? 0 : -1}
              ariaHidden={rotates && i !== current}
            >
              <BannerMedia banner={b} withMobile />
            </BannerBox>
          ))}
        </div>
      </div>

      {/* Controlli solo se c'è davvero qualcosa da scorrere.
          I loro difetti dimensionali (dot 9 × 9, frecce 34 × 34) sono
          l'issue 24 dell'assessment mobile: commit separato. */}
      {rotates && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 hidden sm:flex w-11 h-11 lg:w-8 lg:h-8 rounded-full bg-white/80 backdrop-blur-sm shadow items-center justify-center text-foreground hover:bg-white transition-colors"
            aria-label="Banner precedente"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 hidden sm:flex w-11 h-11 lg:w-8 lg:h-8 rounded-full bg-white/80 backdrop-blur-sm shadow items-center justify-center text-foreground hover:bg-white transition-colors"
            aria-label="Banner successivo"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Dots */}
          <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 z-20 flex items-center">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                /* Area di tocco alta 44px senza ingrandire il pallino: 18px
                   di padding trasparente sopra e sotto (8px di pallino + 36)
                   con lo sfondo ritagliato sul content-box, così il colore
                   resta solo sul pallino. In orizzontale il padding è 5px:
                   con 18px anche lì i pallini finivano a 36px l'uno
                   dall'altro, troppo staccati per leggersi come un gruppo.
                   Su touch il gesto principale ora è lo swipe. */
                className={`box-content rounded-full bg-clip-content py-[18px] px-[5px] transition-all duration-300 motion-reduce:transition-none ${
                  i === current ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Vai al banner ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Slot `sidebar`: colonna verticale a destra della vetrina prodotti.
 *
 * `aspect-[4/5]` + `object-cover` al posto di `h-auto`: era `h-auto` a far
 * dipendere l'altezza resa dal rapporto dell'asset, e con i tre asset
 * attuali (0,558 / 1,000 / 0,671) le card risultavano alte 394 / 220 /
 * 328 px — la colonna irregolare che si vedeva. Ora il rapporto lo impone
 * il layout invece di subirlo.
 *
 * Resta `hidden sm:flex`, quindi invisibile sotto i 640px: per questo Slot
 * non serve un asset mobile. Aggiungere tre banner secondari su telefono
 * peggiorerebbe una homepage già misurata a 7.535 px (8,9 schermate)
 * dall'assessment mobile.
 *
 * Durante il caricamento la colonna si rende con un solo placeholder: serve
 * a riservare la larghezza, altrimenti la griglia prodotti nasce a tutta
 * larghezza e poi si ricompone quando i banner arrivano.
 */
function SidebarBanners() {
  const { banners, loading } = useBanners('sidebar');

  // Slot realmente vuoto: la colonna non si rende affatto e la vetrina
  // prende tutta la larghezza.
  if (!loading && banners.length === 0) return null;

  return (
    <div
      className="hidden sm:flex flex-col gap-3 w-[180px] xl:w-[220px] flex-shrink-0"
      data-testid="hp-banner-sidebar"
    >
      {loading ? (
        <div className="w-full aspect-[4/5] rounded-xl bg-secondary/30" />
      ) : (
        banners.map((b, i) => (
          <BannerBox
            key={b.id}
            banner={b}
            className="relative block w-full aspect-[4/5] rounded-xl overflow-hidden bg-secondary/30 shadow-sm hover:shadow-md transition-shadow"
            testId={`hp-banner-right-${i + 1}`}
          >
            <BannerMedia banner={b} />
          </BannerBox>
        ))
      )}
    </div>
  );
}

// ── Sezione prodotti homepage ──────────────────────────────────────────────
const PAGE_SIZE = 12;

interface HomepageProductsResp {
  items: Product[];
  total: number;
  page: number;
  totalPages: number;
}

// ── Skeleton card — stesse dimensioni di FeaturedProductCard ──────────────
function FeaturedProductCardSkeleton() {
  return (
    <div className="relative bg-white rounded-2xl border-2 border-border shadow-md overflow-hidden flex flex-col animate-pulse">
      {/* Immagine */}
      <div className="h-36 bg-gray-100" />
      {/* Body */}
      <div className="p-3 pt-4 flex-1 flex flex-col">
        {/* SKU + brand */}
        <div className="flex items-center gap-1.5 mb-2">
          <div className="h-3 w-16 bg-gray-200 rounded" />
          <div className="h-3 w-12 bg-gray-200 rounded" />
        </div>
        {/* Title 2 righe */}
        <div className="h-3.5 w-full bg-gray-200 rounded mb-1.5" />
        <div className="h-3.5 w-4/5 bg-gray-200 rounded mb-3" />
        {/* Prezzo */}
        <div className="h-7 w-20 bg-gray-200 rounded mb-2" />
        {/* Stock bar */}
        <div className="mt-auto mb-2.5">
          <div className="flex justify-between mb-1">
            <div className="h-2.5 w-12 bg-gray-200 rounded" />
            <div className="h-2.5 w-8 bg-gray-200 rounded" />
          </div>
          <div className="h-1 w-full bg-gray-100 rounded-full" />
        </div>
        {/* CTA button */}
        <div className="h-9 w-full bg-gray-200 rounded-lg" />
      </div>
    </div>
  );
}

function HomepageProductsSection() {
  const { isLoading: authLoading } = useAuth();
  const [data, setData] = useState<HomepageProductsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const sectionRef = useRef<HTMLDivElement>(null);
  const prevPageRef = useRef(page);

  // Scroll in cima alla sezione dopo il caricamento della nuova pagina —
  // eseguito quando i dati sono effettivamente arrivati (non sullo state
  // `page`) per evitare che l'animazione smooth venga interrotta/clampata
  // dal browser durante il reflow del contenuto. Scrolla SOLO se `page` è
  // realmente cambiato rispetto al render precedente (non al caricamento
  // iniziale dei dati).
  useEffect(() => {
    if (data && prevPageRef.current !== page) {
      const el = sectionRef.current;
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    prevPageRef.current = page;
  }, [data]);

  useEffect(() => {
    // Attende che il restore della sessione (token in localStorage) sia
    // completato prima di interrogare i prezzi — bug 26/02/2026 rilevato dal
    // testing agent: su hard-refresh la prima chiamata partiva SENZA header
    // Authorization (AuthContext ancora in fase di restore), quindi il
    // backend (ora corretto per nascondere i prezzi agli anonimi) restituiva
    // prezzo nullo anche per un cliente loggato/approvato.
    if (authLoading) return;
    let cancelled = false;
    setLoading(true);
    api.get<HomepageProductsResp>('/api/homepage-products', { params: { page, page_size: PAGE_SIZE } })
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [page, authLoading]);

  if (!loading && (!data || data.total === 0)) return null;

  const totalPages = data?.totalPages ?? 1;

  return (
    <section className="pt-4 pb-10 sm:pb-14 border-b bg-white" data-testid="homepage-products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" ref={sectionRef}>
        {/* Titolo sezione */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#2B5EA7]">
              In evidenza
            </span>
            <h2 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight">
              Prodotti selezionati
            </h2>
          </div>
          <Link
            to="/prodotti/tutti"
            className="hidden sm:flex items-center gap-1.5 text-sm text-[#2B5EA7] hover:underline"
          >
            Vedi tutto il catalogo <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Layout: prodotti + colonna banner destra */}
        <div className="flex gap-5 items-start">

          {/* Colonna centrale: prodotti + paginazione */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                data-testid="homepage-products-skeleton"
              >
                {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <FeaturedProductCardSkeleton key={i} />
                ))}
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                data-testid="homepage-products-grid"
              >
                {(data?.items ?? []).map(p => {
                  const price = (p.listino10 ?? 0).toFixed(2).replace('.', ',');
                  const stock = (p.stock as number | undefined) ?? 0;
                  return (
                    <FeaturedProductCard
                      key={p.sku}
                      product={p}
                      priceCurrent={price}
                      stock={stock}
                    />
                  );
                })}
              </div>
            )}

            {/* Paginazione numerata */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-1.5" data-testid="homepage-products-pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-9 w-9 rounded-lg border bg-white hover:bg-secondary/50 disabled:opacity-40 flex items-center justify-center transition-colors"
                  aria-label="Pagina precedente"
                  data-testid="hp-page-prev"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    data-testid={`hp-page-${n}`}
                    className={`h-9 w-9 rounded-lg border text-sm font-medium transition-colors ${
                      n === page
                        ? 'bg-[#2B5EA7] text-white border-[#2B5EA7]'
                        : 'bg-white hover:bg-secondary/50'
                    }`}
                    aria-label={`Pagina ${n}`}
                    aria-current={n === page ? 'page' : undefined}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-9 w-9 rounded-lg border bg-white hover:bg-secondary/50 disabled:opacity-40 flex items-center justify-center transition-colors"
                  aria-label="Pagina successiva"
                  data-testid="hp-page-next"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Colonna banner destra — nascosta su mobile (1 col), visibile da sm in su */}
          <SidebarBanners />

        </div>

      </div>
    </section>
  );
}

// ── Pagina homepage ────────────────────────────────────────────────────────
export default function HomePage() {
  const hero = useBanners('hero');
  // Durante il caricamento lo Slot resta prenotato: i banner ci sono quasi
  // sempre, quindi riservare lo spazio evita che il widget nasca a tutta
  // larghezza per poi restringersi. Collassa solo se lo Slot è davvero vuoto.
  const heroVisible = hero.loading || hero.banners.length > 0;

  return (
    <div data-testid="home-page">

      {/* ── Hero: Ricerca avanzata + Banner carousel ─────────────── */}
      <section className="relative bg-white">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{backgroundImage:'url("data:image/svg+xml,%3Csvg viewBox=%270 0 256 256%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cfilter id=%27noise%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%274%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23noise)%27/%3E%3C/svg%3E")'}} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* Colonna sinistra: widget Ricerca avanzata — altezza
                naturale/compatta (nessuno spacer interno), è questa la
                misura di riferimento per il banner (richiesta utente
                24/02/2026). Senza banner attivi prende tutta la larghezza:
                uno Slot vuoto collassa invece di lasciare un rettangolo
                grigio, che somiglierebbe a un guasto. */}
            <div className={`${heroVisible ? 'lg:col-span-4 xl:col-span-4' : 'lg:col-span-12'} order-2 lg:order-1 flex flex-col`}>
              <RicercaAvanzataWidget className="flex-1" />
            </div>
            {/* Colonna destra: Banner — si stira fino a pareggiare
                l'altezza naturale del widget a sinistra. */}
            {/* `lg:aspect-[16/9]` sulla COLONNA, non sul box: dà alla riga
                della griglia un pavimento pari a un 16:9, senza togliere al
                box la possibilità di essere più alto.

                La riga vale quindi max(widget, 16:9) e il box la riempie
                sempre — nessuna banda vuota sotto il banner a 1024px, dove
                il widget è il più alto dei due, e nessun ritaglio verticale
                da 1280px in su, dove il rapporto del box è esattamente
                quello degli asset. È un pavimento *dichiarato*: non
                dipende dalle immagini caricate, quindi due slide con
                rapporti diversi non possono più alzare lo slot. */}
            {heroVisible && (
              <div className="lg:col-span-8 xl:col-span-8 order-1 lg:order-2 flex flex-col lg:w-full lg:aspect-[16/9]">
                <HeroBanners banners={hero.banners} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CTA registrazione (ora include anche i value props sotto,
          richiesta utente 27/02/2026 — prima era una sezione separata) ── */}
      <section className="pt-3 sm:pt-4 pb-3 sm:pb-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1E4D8C] to-[#2B5EA7] p-4 sm:p-5 shadow-lg" data-testid="reseller-cta-banner">
            <div
              className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-amber-400/10 pointer-events-none"
              aria-hidden
            />
            <div className="flex flex-col md:flex-row items-center gap-4 relative z-10">
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-1 text-white">Sei un rivenditore?</h2>
                <p className="text-white/80">
                  Registrati per accedere al catalogo completo con prezzi riservati, disponibilità in tempo reale e ordini online.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Link
                  to="/registrati"
                  data-testid="reseller-cta-register"
                  className="group relative h-11 px-6 rounded-lg bg-gradient-to-r from-amber-300 to-amber-400 text-[#1E4D8C] text-sm font-bold flex items-center gap-2 overflow-hidden hover:from-amber-400 hover:to-amber-500 hover:scale-[1.03] shadow-sm hover:shadow-md transition-all"
                >
                  Registrati ora <ArrowRight className="w-4 h-4" />
                  <span
                    className="absolute -inset-y-0 -inset-x-1 -skew-x-12 bg-white/30 w-6 opacity-0 group-hover:opacity-100 group-hover:translate-x-[160%] transition-all duration-700 ease-out"
                    aria-hidden
                  />
                </Link>
                <Link
                  to="/supporto/catalogo-online"
                  data-testid="reseller-cta-catalog"
                  className="h-11 px-6 rounded-lg border-2 border-white/50 text-white text-sm font-medium flex items-center hover:border-amber-400 hover:bg-amber-400 hover:text-[#1E4D8C] transition-colors"
                >
                  Scopri il catalogo
                </Link>
              </div>
            </div>

            {/* Value props — accorpati dentro il banner blu, sotto il testo/CTA */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/15 relative z-10" data-testid="reseller-cta-value-props">
              {VALUE_PROPS.map(vp => {
                const Icon = ICON_MAP[vp.icon];
                return (
                  <div key={vp.title} className="flex items-start gap-3">
                    {Icon && (
                      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-amber-300" />
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-white">{vp.title}</div>
                      <div className="text-xs text-white/70">{vp.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Prodotti selezionati (da admin) ──────────────────────── */}
      <HomepageProductsSection />

      {/* ── Visti di recente ─────────────────────────────────────── */}
      <RecentlyViewed />
    </div>
  );
}
